import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { SessionProvider } from './contexts/SessionContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

// Auth Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';

// Dashboard
import DashboardPage from './pages/DashboardPage';

// Admin Pages
import ClassesPage from './pages/admin/ClassesPage';
import StudentsPage from './pages/admin/StudentsPage';
import TeachersPage from './pages/admin/TeachersPage';
import SubjectsPage from './pages/admin/SubjectsPage';
import FeesPage from './pages/admin/FeesPage';
import AttendancePage from './pages/admin/AttendancePage';
import ExamsPage from './pages/admin/ExamsPage';
import HomeworkPage from './pages/admin/HomeworkPage';
import AnnouncementsPage from './pages/admin/AnnouncementsPage';
import ClassTeacherPage from './pages/admin/ClassTeacherPage';
import SettingsPage from './pages/admin/SettingsPage';
import BirdAIPage from './pages/admin/BirdAIPage';
import DiscountWorkflowPage from './pages/admin/DiscountWorkflowPage';
import StudentDetailPage from './pages/admin/StudentDetailPage';

// Internal Pages
import OnboardingPage from './pages/internal/OnboardingPage';

// Student Pages
import StudentFeesPage from './pages/StudentFeesPage';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <SessionProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            
             {/* Dashboard - All authenticated users */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout><DashboardPage /></Layout>
              </ProtectedRoute>
            } />

            {/* Super Admin Routes */}
            <Route path="/internal/onboarding" element={
              <ProtectedRoute allowedRoles={['superadmin']}>
                <Layout><OnboardingPage /></Layout>
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin/classes" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><ClassesPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/admin/students" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><StudentsPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/students/:id" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><StudentDetailPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/admin/teachers" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><TeachersPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/admin/subjects" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><SubjectsPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/admin/fees" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><FeesPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/admin/attendance" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><AttendancePage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/admin/exams" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><ExamsPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/admin/homework" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><HomeworkPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/admin/announcements" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><AnnouncementsPage /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/admin/class-teacher" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><ClassTeacherPage /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/admin/bird-ai" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><BirdAIPage /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/admin/fee-workflow" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><DiscountWorkflowPage /></Layout>
              </ProtectedRoute>
            } />

            <Route path="/admin/settings" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout><SettingsPage /></Layout>
              </ProtectedRoute>
            } />

            {/* Teacher Routes */}
            <Route path="/teacher/attendance" element={
              <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                <Layout><AttendancePage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/teacher/marks" element={
              <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                <Layout><ExamsPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/teacher/homework" element={
              <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                <Layout><HomeworkPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/teacher/announcements" element={
              <ProtectedRoute allowedRoles={['admin', 'teacher']}>
                <Layout><AnnouncementsPage /></Layout>
              </ProtectedRoute>
            } />

            {/* Student Routes */}
            <Route path="/student/fees" element={
              <ProtectedRoute allowedRoles={['admin', 'teacher', 'student']}>
                <Layout><StudentFeesPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/student/homework" element={
              <ProtectedRoute allowedRoles={['admin', 'teacher', 'student']}>
                <Layout><HomeworkPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/student/announcements" element={
              <ProtectedRoute allowedRoles={['admin', 'teacher', 'student']}>
                <Layout><AnnouncementsPage /></Layout>
              </ProtectedRoute>
            } />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </SessionProvider>
    </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
